import * as React from "react";
import Paper from "@mui/material/Paper";
import moment from "moment";
import {
  Button,
  Divider,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Pagination,
  PaginationItem,
} from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import API from "../../Api/api";
import { useSelector } from "react-redux";

const columns = [
  { field: "code", headerName: "Asset Code", minWidth: 100 },
  {
    field: "pool",
    headerName: "ASSET POOL",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "location",
    headerName: "LOCATION",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "sub_location",
    headerName: "SUB LOCATION",
    minWidth: 170,
    align: "right",
    format: (value) => value.toFixed(2),
  },
  {
    field: "serial",
    headerName: "SERIAL/CHASSIS NUMBER",
    minWidth: 170,
    align: "right",
    format: (value) => value.toFixed(2),
  },
  {
    field: "cost",
    headerName: "COST",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "accum_depn",
    headerName: "TOTAL AMORTIZATION",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "carrying_amount",
    headerName: "CARRYING AMOUNT",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "disposal_value",
    headerName: "DISPOSAL VALUE",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "profit_loss",
    headerName: "PROFIT/LOSS",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "disposal_date",
    headerName: "DISPOSAL DATE",
    minWidth: 170,
    align: "right",
    format: (value) => value.toFixed(2),
  },
  {
    field: "disposal_reason",
    headerName: "DISPOSAL REASON",
    minWidth: 170,
    align: "right",
    format: (value) => value.toFixed(2),
  },
  {
    field: "disposal_method",
    headerName: "DISPOSAL METHOD",
    minWidth: 170,
    align: "right",
    format: (value) => value.toFixed(2),
  },
];

function createData(
  code,
  pool,
  location,
  sub_location,
  serial,
  cost,
  accum_depn,
  disposal_value,
  disposal_date,
  disposal_reason,
  disposal_method,
  purchase_date
) {
  const carrying_amount = cost * 1 - accum_depn * 1;
  const profit_loss = accum_depn * 1 + disposal_value * 1;
  return {
    code,
    pool,
    location,
    sub_location,
    serial,
    cost,
    accum_depn,
    carrying_amount,
    disposal_value,
    profit_loss,
    disposal_date,
    disposal_reason,
    disposal_method,
    purchase_date,
  };
}

export default function IntangibleDisposalAssetReport() {
  const mode_state = useSelector((state) => state.mode);
  const [mode, setMode] = React.useState(mode_state);
  React.useEffect(() => {
    setMode(mode_state);
  }, [mode_state]);
  const [message, setMessage] = React.useState(false);
  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color='primary'
        sx={{ bgcolor: "#ffffff" }}
        variant='outlined'
        shape='rounded'
        page={page + 1}
        count={pageCount}
        // @ts-expect-error
        renderItem={(props2) => <PaginationItem {...props2} disableRipple />}
        onChange={(event, value) => apiRef.current.setPage(value - 1)}
      />
    );
  }

  const [data, setData] = React.useState([]);

  const [filterData, setFilterData] = React.useState({
    start_date: "",
    end_date: "",
  });

  React.useEffect(() => {
    const fetchData = async () => {
      setMessage(true);
      const res = await API.get(
        `/api/v1/company-assets/ppe/ppe-section?approved=true&disposed=true&category[eq]=intangibles`
      );
      if (res.data[0]) {
        setData(res.data);
        setMessage(false);
      } else {
        setData([]);
        setMessage(false);
      }
    };
    fetchData();
  }, []);
  // ! declaring of queries so that we can chain them together// Start
  const start = filterData.start_date
    ? `&disposal_date[gte]=${filterData.start_date}`
    : "";
  const end = filterData.end_date
    ? `&disposal_date[lte]=${filterData.end_date}`
    : "";
  const query = `/api/v1/company-assets/ppe/ppe-section?approved=true&category[eq]=intangibles&disposed=true${start}${end}`;
  // function to fetch the data from the database
  const fetchData = async () => {
    if (filterData.start_date || filterData.end_date) {
      setMessage(true);
      const res = await API.get(query);

      if (res.data) {
        setData(res.data);
        setMessage(false);
      } else {
        setData([]);
        alert("No results found");
        setMessage(false);
      }
    } else {
      alert("Select either start or end date or the two to generate report");
    }
  };
  // !----------------End--------------------

  const rows = data
    ? data.map((data) => {
        return createData(
          data.code,
          data.category,
          data.location,
          data.sub_location,
          data.serial_number,
          data.cost,
          data.accumulated_depreciation,
          data.disposal_value,
          moment(data.disposal_date).format("YYYY-MM-DD"),
          data.disposal_reason,
          data.disposal_method,
          data.purchase_date
        );
      })
    : [];

  return (
    <div style={{ height: "81vh" }}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={message}
        autoHideDuration={6000}>
        <Alert severity='info'>
          Loading <span className='blinking-text'>.....</span>
        </Alert>
      </Snackbar>
      <div
        style={{
          fontSize: "14px",
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: "10px",
          // alignItems: "center",
          // justifyContent: "space-between",
        }}>
        <Typography
          variant='body1'
          htmlFor='start-date'
          style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}>
          Start Date
        </Typography>
        <TextField
          size='small'
          id='start-date'
          style={{ backgroundColor: "#ccc", borderRadius: "5px" }}
          type='date'
          value={filterData.start_date}
          onChange={(e) =>
            setFilterData({
              ...filterData,
              start_date: e.target.value,
            })
          }
        />
        <Typography
          variant='body1'
          htmlFor='end-date'
          style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}>
          End Date
        </Typography>
        <TextField
          size='small'
          id='end-date'
          style={{ backgroundColor: "#ccc", borderRadius: "5px" }}
          type='date'
          value={filterData.end_date}
          onChange={(e) =>
            setFilterData({
              ...filterData,
              end_date: e.target.value,
            })
          }
        />
        <Button
          onClick={fetchData}
          style={{
            marginLeft: "10px",
            textTransform: "none",
            backgroundColor: `${mode === "dark" ? "#0883FF" : "#116149"}`,
          }}
          variant='contained'>
          Generate Report
        </Button>
      </div>

      <Divider style={{ marginBottom: "10px" }} />
      <Paper sx={{ width: "100%", height: "70vh" }}>
        <DataGrid
          columns={columns}
          rows={rows}
          style={{
            backgroundColor: `${mode === "dark" ? "#101418" : "#ffffff"}`,
            color: `${mode === "dark" ? "#fff" : "#000"}`,
          }}
          sx={{
            boxShadow: 2,
            border: 1,
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-footerCell": {
              backgroundColor: "primary.main",
            },
            "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
              backgroundColor: mode === "light" ? "#fafafa" : "#1d1d1d",
              borderBottom: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              borderRight: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-cell": {
              color:
                mode === "light" ? "rgba(0,0,0,.85)" : "rgba(255,255,255,0.65)",
            },
          }}
          slots={{ toolbar: GridToolbar, pagination: CustomPagination }}
        />
      </Paper>
    </div>
  );
}
